import React, { lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "react-query";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SnackbarProvider } from "notistack";
import { ConfirmProvider } from "material-ui-confirm";
import PrivateRoute from "./components/routing/PrivateRoute";
import RoleRoute from "components/routing/RoleRoute";
import DashboardLayout from "./layouts/Dashboard/Dashboard";
import MinimalLayout from "layouts/Minimal/Minimal";
import Login from "./pages/login/Login";
const CreateAccount = lazy(() => import("pages/create-account/CreateAccount"));
const ForgotPassword = lazy(() =>
  import("pages/forgot-password/ForgotPassword")
);
const ResetPassword = lazy(() => import("pages/reset-password/ResetPassword"));
const CompaniesList = lazy(() => import("./pages/companies/List"));
const TendersList = lazy(() => import("./pages/contracts/ListTenders"));
const ContractsList = lazy(() => import("./pages/contracts/ListContracts"));
const OthersList = lazy(() => import("./pages/contracts/ListOthers"));
const ContractualNoticesList = lazy(() =>
  import("./pages/contracts/ListContractualNotices")
);
const PublicList = lazy(() => import("./pages/contracts/ListPublic"));
const ReviewsList = lazy(() => import("./pages/reviews/List"));
const ManageAdminUsers = lazy(() => import("pages/administrators/List"));
const ManagePublicUsers = lazy(() => import("pages/publicusers/List"));
const UsersList = lazy(() => import("./pages/users/List"));
const TemplatesListTender = lazy(() =>
  import("./pages/templates/TemplatesListTender")
);
const TemplatesListContract = lazy(() =>
  import("./pages/templates/TemplatesListContract")
);
const TemplatesListOther = lazy(() =>
  import("./pages/templates/TemplatesListOther")
);
const TemplatesListContractualNotice = lazy(() =>
  import("./pages/templates/TemplatesListContractualNotice")
);
const TemplatesListPublic = lazy(() =>
  import("./pages/templates/TemplatesListPublic")
);
const TemplateSections = lazy(() =>
  import("pages/template-sections/TemplateSections")
);
const TemplateQuestions = lazy(() =>
  import("pages/template-questions/TemplateQuestions")
);
const CreateForSection = lazy(() => "pages/pdf/CreateForSection");
const CreateForTemplate = lazy(() => import("pages/pdf/CreateForTemplate"));
const ProjectsList = lazy(() => import("pages/projects/List"));
const SubProjectsList = lazy(() => import("pages/subprojects/List"));
const DraggableList = lazy(() => import("pages/testing/DraggableList"));
const TreeView = lazy(() => import("pages/testing/TreeView"));
const ReviewContract = lazy(() => import("pages/reviews/Review"));
const RedirectRoute = lazy(() => import("components/routing/RedirectRoute"));
const VerifyUser = lazy(() => import("pages/users/Verify"));
const AcceptTermsAndConditions = lazy(() =>
  import("pages/users/AcceptTermsAndConditions")
);
const ViewTermsAndConditions = lazy(() =>
  import("pages/view-tnc/ViewTermsAndConditions")
);
const UploadUserManual = lazy(() =>
  import("pages/upload-user-manual/UploadUserManual")
);
const ViewUserManual = lazy(() =>
  import("pages/upload-user-manual/ViewUserManual")
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider maxSnack={3}>
          <ConfirmProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<MinimalLayout />}>
                  <Route path="login" element={<Login />} />
                  <Route path="create-account" element={<CreateAccount />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="contract-review" element={<ReviewContract />} />
                  <Route path="verify" element={<VerifyUser />} />
                </Route>

                <Route path="/*" element={<MinimalLayout maxWidth="900px" />}>
                  <Route
                    path="accept-tnc"
                    element={<AcceptTermsAndConditions />}
                  />
                </Route>

                <Route path="/*" element={<PrivateRoute />}>
                  <Route element={<DashboardLayout />}>
                    <Route
                      path="administrators"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <ManageAdminUsers />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="publicusers"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <ManagePublicUsers />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="companies"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <CompaniesList />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="upload-user-manual"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <UploadUserManual />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="companies/:id/users"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <UsersList />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="tender-templates"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplatesListTender />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="contract-templates"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplatesListContract />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="other-templates"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplatesListOther />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="contractual-notice-templates"
                      element={<TemplatesListContractualNotice />}
                    />
                    <Route
                      path="public-templates"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplatesListPublic />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="templates/:templateId/sections"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplateSections />
                        </RoleRoute>
                      }
                    />
                    <Route
                      path="templates/:templateId/questions"
                      element={
                        <RoleRoute allowedRoles={["admin"]}>
                          <TemplateQuestions />
                        </RoleRoute>
                      }
                    />
                    <Route path="projects" element={<ProjectsList />} />
                    <Route
                      path="projects/:id/subprojects"
                      element={<SubProjectsList />}
                    />
                    <Route path="tenders" element={<TendersList />} />
                    <Route path="contracts" element={<ContractsList />} />
                    <Route path="others" element={<OthersList />} />
                    <Route
                      path="contractual-notices"
                      element={<ContractualNoticesList />}
                    />
                    <Route path="public" element={<PublicList />} />
                    <Route path="reviews" element={<ReviewsList />} />
                    <Route
                      path="pdf/create-for-section"
                      element={
                        <CreateForSection templateId="1" sectionId="1" />
                      }
                    />
                    <Route
                      path="pdf/create-for-template"
                      element={<CreateForTemplate templateId="1" />}
                    />
                    <Route
                      path="view-tnc"
                      element={<ViewTermsAndConditions />}
                    />
                    <Route
                      path="view-user-manual"
                      element={<ViewUserManual />}
                    />
                    <Route
                      path="testing/draggable-list"
                      element={<DraggableList />}
                    />
                    <Route path="testing/treeview" element={<TreeView />} />
                    <Route
                      path="*"
                      element={<RedirectRoute />}
                      replace={true}
                    />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </ConfirmProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </LocalizationProvider>
  );
}
